import React, { useCallback, useMemo } from 'react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { Typography, Stack } from '@mui/material'
import { StripeElementsOptionsClientSecret } from '@stripe/stripe-js/dist/stripe-js/elements-group'
import StripePaymentElement from '../../../../../components/StripePaymentElement/StripePaymentElement'

import {
	actionCreateUser,
	actionResetStripeCustomerData,
	selectCreateUserStatus,
	selectOnboardingData,
} from '../../../../../store/slices/user/userSlice'
import { Status } from '../../../../../store/types'
import OnboardingNavigation from '../../../components/OnboardingNavigation/OnboardingNavigation'
import { useAppDispatch, useAppSelector } from '../../../../../store/utils/hooks'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import urlBuilder from '../../../../../utils/UrlBuilder'
import { ONBOARDING_PATH, VALIDATION_PATH } from '../../../../../routes/OnboardingRoutes'
import { trackLead } from '../../../../../utils/Affiliate/loadAffiliateVisitorScript'

type PaymentSetupProps = {
	options: StripeElementsOptionsClientSecret
	validAccount: boolean
}

export default function PaymentSetup({ options, validAccount }: PaymentSetupProps) {
	const { palette } = useTheme()
	const elements = useElements()
	const stripe = useStripe()
	const dispatch = useAppDispatch()
	const createUserStatus = useAppSelector(selectCreateUserStatus)
	const onboardingData = useAppSelector(selectOnboardingData)
	const [isComplete, setIsComplete] = React.useState(false)
	const [message, setMessage] = React.useState<string | null>(null)
	const [isProcessingPayment, setIsProcessingPayment] = React.useState(false)
	const navigate = useNavigate()

	const processPayment = useCallback(async () => {
		if (!stripe || !elements) {
			// Stripe.js has not yet loaded.
			return
		}

		setIsProcessingPayment(true)
		const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' })

		if (error) {
			setMessage(error.message || 'An unexpected error occurred.')
			setIsProcessingPayment(false)
		} else {
			setMessage(null)
			return setupIntent
		}
	}, [stripe, elements])

	const navBack = useCallback(() => navigate(ONBOARDING_PATH), [navigate])

	const navForward = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault()
			const setupIntent = await processPayment()
			if (setupIntent && setupIntent.status === 'succeeded') {
				setIsProcessingPayment(false)
				trackLead(onboardingData.firstName, onboardingData.lastName, onboardingData.email)
				dispatch(actionCreateUser({ ...onboardingData, setup_intent_id: setupIntent.id }))
				dispatch(actionResetStripeCustomerData())
				navigate(urlBuilder([ONBOARDING_PATH, VALIDATION_PATH]))
			}
		},
		[dispatch, navigate, onboardingData, processPayment],
	)

	const disableForward = useMemo(() => !validAccount || !isComplete, [isComplete, validAccount])

	const handlePaymentFormCompletion = useCallback((isComplete: boolean) => {
		if (isComplete) {
			setMessage(null)
		}
		setIsComplete(isComplete)
	}, [])

	if (!options.clientSecret) {
		return <Typography color="error">Missing client secret</Typography>
	}

	return (
		<Stack spacing={3}>
			<StripePaymentElement
				setIsComplete={handlePaymentFormCompletion}
				palette={palette}
				message={message}
				sx={{ mb: 4 }}
			/>
			<OnboardingNavigation
				i18nForwardLabel="onboarding.payment.subscribe_button_label"
				backClick={navBack}
				forwardClick={navForward}
				disableForward={disableForward}
				loading={createUserStatus === Status.PENDING || isProcessingPayment}
			/>
		</Stack>
	)
}
